import { Component, Prop, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import GridDisplayOptions from '@/components/GridDisplayOptions.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import ModalWrapper from '@/components/ModalWrapper.vue'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import {
    SubportionStateMachineConfigurationCreateRequest,
    SubportionStateMachineConfigurationPatchInBatchRequest,
    SubportionStateMachineConfigurationResponse
} from '@/modules/configurationModule/detail/local-laboratory-module/subportion-state-machines/services/configuration-subportion-state-machines/configuration-subportion-state-machines.dto'
import {
    initialSubportionStateMachinesGridState,
    SubportionStateMachinesGridState
} from '@/modules/configurationModule/detail/local-laboratory-module/subportion-state-machines/components/grid/configuration-subportion-state-machines-grid.model'
import ConfigurationSubportionStateMachinesModal
    from '@/modules/configurationModule/detail/local-laboratory-module/subportion-state-machines/components/modal/configuration-subportion-state-machines-modal.vue'

@Component({
    components: {
        ConfigurationSectionHeader,
        GridDisplayOptions,
        VtGrid,
        ConfigurationSubportionStateMachinesModal
    }
})
export default class ConfigurationSubportionStateMachinesGrid extends BaseCrudServerSideComponent<SubportionStateMachineConfigurationResponse> implements BaseCrud {
    @Ref() readonly subportionStateMachinesModalWrapper!: typeof ModalWrapper
    @Ref() readonly subportionStateMachinesModalContent!: any
    @Prop() readonly sectionId!: number

    state: SubportionStateMachinesGridState

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    constructor() {
        super()
        this.state = initialSubportionStateMachinesGridState()
    }

    mounted() {
        if (this.sectionId) {
            this.state.sectionId = this.sectionId
            this.onAdd()
        }
    }

    /// SERVICES CALLS ///

    private async _createSubportionStateMachineConfiguration(createRequest: SubportionStateMachineConfigurationCreateRequest): Promise<SubportionStateMachineConfigurationResponse | null> {
        const response = await this.$_toCallUnique(this.state.service.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            this.subportionStateMachinesModalContent.hide()
            return response
        }
        return null
    }

    private async _patchSubportionStateMachineConfiguration(batchRequests: SubportionStateMachineConfigurationPatchInBatchRequest[]): Promise<EntityIdentifierWithErrorResponse[] | null> {
        if (batchRequests) {
            const response = await this.$_toCallUnique(this.state.service.patch(this.state.laboratoryId, batchRequests))
            if (response) {
                const elements = batchRequests.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
                    this.subportionStateMachinesModalContent.hide()
                }
                return response
            }
        }
        return null
    }

    private async _deleteSubportionStateMachineConfiguration(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///

    async createOperation(createRequest: SubportionStateMachineConfigurationCreateRequest): Promise<void> {
        if (createRequest) {
            if (await this._createSubportionStateMachineConfiguration(createRequest)) {
                this.reloadData()
            }
        }
    }

    async patchOperation(batchRequest: SubportionStateMachineConfigurationPatchInBatchRequest): Promise<void> {
        if (batchRequest) {
            if (await this._patchSubportionStateMachineConfiguration([batchRequest])) {
                this.reloadData()
            }
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteSubportionStateMachineConfiguration(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///

    onUpdate(subportionStateMachineConfiguration: SubportionStateMachineConfigurationResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (subportionStateMachineConfiguration) {
            this.showModal(this.$tc('configuration.actions.update'), subportionStateMachineConfiguration, this.subportionStateMachinesModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(subportionStateMachineConfiguration => subportionStateMachineConfiguration.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.subportionStateMachinesModalWrapper)
    }

    onHideModal(): void {
        this.state.sectionId = null
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            }
        ]
    }
}
